/**
 * Strapify — Watch Selector Cart Integration
 * ============================================
 * 1. Reads the selectedWatch from localStorage (set by the watch selector bar)
 * 2. On Add to Cart, injects line item properties:
 *      "For Watch"              → visible to customer, removable
 *      "_Strap Size (Watch)"    → hidden from customer
 *      "_⚠ Size Override"       → hidden, only written on confirmed mismatch
 * 3. Validates strap size against watch size for eligible SKUs
 * 4. Shows a popup on mismatch with option to swap variant (Option A) or keep
 * 5. In cart: renders "For Watch" badge with a ✕ to remove all three properties
 *
 * VALIDATION RULES
 *  - Only validates SKUs starting with "z-" or "qr" (case-insensitive)
 *  - Skips: forstner, strapcode, staib, uncle, strapxpro anywhere in SKU
 *  - Skips: buckle, clasp, adapter anywhere in SKU or product title
 *  - Only fires when a watch IS selected in localStorage
 *  - Scenario B (no watch selected) → silent, no prompt
 */

(function () {
  'use strict';

  // Prevent double-initialisation if script is loaded more than once
  if (window.__strapifyCartInit) return;
  window.__strapifyCartInit = true;

  /* ─────────────────────────────────────────────
     CONFIG
  ───────────────────────────────────────────── */
  const PROP_WATCH      = 'For Watch';
  const PROP_SIZE       = '_Strap Size (Watch)';
  const PROP_OVERRIDE   = '_⚠ Size Override';

  // SKU prefixes that get validated (case-insensitive)
  const VALIDATE_PREFIXES = ['z-', 'qr'];

  // SKU prefixes that skip watch note injection entirely (no "For Watch" property, no popup)
  const SKIP_INJECTION_PREFIXES = ['s-', 'zx-', 'za-'];

  // If any of these appear anywhere in the SKU → skip validation
  const SKIP_BRANDS = ['forstner', 'strapcode', 'staib', 'uncle', 'strapxpro'];

  // If any of these appear in SKU or product title → skip validation
  const SKIP_PRODUCT_TYPES = ['buckle', 'clasp', 'adapter'];

  /* ─────────────────────────────────────────────
     UTILITIES
  ───────────────────────────────────────────── */

  // Cache watch in memory — only re-read on storage events
  let _cachedWatch = undefined;
  function getSelectedWatch() {
    if (_cachedWatch !== undefined) return _cachedWatch;
    try {
      const raw = localStorage.getItem('selectedWatch');
      _cachedWatch = raw ? JSON.parse(raw) : null;
    } catch (e) {
      _cachedWatch = null;
    }
    return _cachedWatch;
  }
  window.addEventListener('storage', (e) => {
    if (e.key === 'selectedWatch') _cachedWatch = undefined;
  });

  /** Extract leading numeric mm value from a size string e.g. "20mm Quick Release" → 20 */
  function extractMm(str) {
    if (!str) return null;
    const m = String(str).match(/\b(\d+(?:\.\d+)?)\s*mm\b/i);
    return m ? parseFloat(m[1]) : null;
  }

  /** Should the "For Watch" note be injected at all for this SKU? */
  function shouldInjectWatchNote(sku) {
    if (!sku) return true;
    const skuLower = sku.toLowerCase();
    return !SKIP_INJECTION_PREFIXES.some(p => skuLower.startsWith(p));
  }

  /** Should this SKU/product be validated? */
  function shouldValidate(sku, productTitle) {
    if (!sku) return false;
    const skuLower   = sku.toLowerCase();
    const titleLower = (productTitle || '').toLowerCase();

    // Must start with a validate prefix
    const hasPrefix = VALIDATE_PREFIXES.some(p => skuLower.startsWith(p));
    if (!hasPrefix) return false;

    // Skip branded exceptions
    if (SKIP_BRANDS.some(b => skuLower.includes(b))) return false;

    // Skip product type exceptions
    if (SKIP_PRODUCT_TYPES.some(t => skuLower.includes(t) || titleLower.includes(t))) return false;

    return true;
  }

  /** Find a variant element/option that matches a target mm value */
  function findVariantForMm(targetMm) {
    // Impulse theme: block-swatch labels (pure mm only, e.g. "18mm", "20mm")
    const swatchLabels = document.querySelectorAll('label.block-swatch');
    for (const label of swatchLabels) {
      const sizeText = label.textContent?.trim();
      if (!sizeText || !sizeText.match(/^\d+mm$/)) continue;
      if (extractMm(sizeText) === targetMm) return label;
    }
    // Shopify variant option buttons or swatches — common selectors
    const candidates = document.querySelectorAll(
      '[data-option-value], .product-form__option input[type="radio"], .variant-input input[type="radio"], .swatch input[type="radio"]'
    );
    for (const el of candidates) {
      const label = el.getAttribute('data-option-value') ||
                    el.value ||
                    el.closest('label')?.textContent || '';
      if (extractMm(label) === targetMm) return el;
    }
    // Also check visible button labels
    const btns = document.querySelectorAll(
      '.product-form__option label, .variant-input label, .swatch label, [data-option-value]'
    );
    for (const btn of btns) {
      if (extractMm(btn.textContent) === targetMm) return btn;
    }
    return null;
  }

  /** Click / activate a variant element to swap the selection on the page */
  function activateVariant(el) {
    if (!el) return false;
    // If it's an input, click its label or the input itself
    if (el.tagName === 'INPUT') {
      el.click();
      el.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }
    // If it's a label or button
    el.click();
    return true;
  }

  /** Get the currently selected variant's size option value */
  function getSelectedVariantSize() {
    // Impulse theme: block-swatch labels (pure mm, e.g. "18mm") — check first as it's the known theme
    const swatchLabels = document.querySelectorAll('label.block-swatch');
    for (const label of swatchLabels) {
      const sizeText = label.textContent?.trim();
      if (!sizeText || !sizeText.match(/^\d+mm$/)) continue;
      const inputId = label.getAttribute('for');
      if (inputId) {
        const input = document.getElementById(inputId);
        if (input && (input.checked || input.getAttribute('aria-checked') === 'true')) {
          return sizeText;
        }
      }
    }
    // Fallback: standard radio inputs
    const checked = document.querySelector(
      '.product-form__option input[type="radio"]:checked, .variant-input input[type="radio"]:checked'
    );
    if (checked) {
      return checked.getAttribute('data-option-value') || checked.value || checked.closest('label')?.textContent?.trim() || null;
    }
    // Fallback: select element
    const selects = document.querySelectorAll('select[name*="option"], select[id*="option"]');
    for (const s of selects) {
      const val = s.options[s.selectedIndex]?.text || s.value;
      if (val && extractMm(val) !== null) return val;
    }
    return null;
  }

  // Cache product info per variant — SKU changes per variant, title doesn't
  let _cachedProductInfo = null;
  let _cachedVariantId   = null;

  /** Get the current product's SKU and title from the page */
  function getProductInfo() {
    const vId = getSelectedVariantId();

    // Return cache if variant hasn't changed
    if (_cachedProductInfo && vId === _cachedVariantId) return _cachedProductInfo;
    _cachedVariantId = vId;

    let sku   = null;
    let title = document.title || '';

    // Try global product JSON (Impulse exposes this)
    if (window.__strapify_productJSON) {
      const variant = window.__strapify_productJSON.variants?.find(v => v.id === vId);
      if (variant?.sku) sku = variant.sku;
      if (window.__strapify_productJSON.title) title = window.__strapify_productJSON.title;
    }

    // Impulse theme: data-block-type="sku"
    if (!sku) {
      const impulseSkuBlock = document.querySelector('[data-block-type="sku"]');
      if (impulseSkuBlock) {
        const match = impulseSkuBlock.textContent.match(/SKU\s+(\S+)/i);
        if (match) sku = match[1].trim();
      }
    }

    // Fallback: meta tag
    if (!sku) {
      const metaSku = document.querySelector('meta[itemprop="sku"], [data-product-sku]');
      if (metaSku) sku = metaSku.getAttribute('content') || metaSku.dataset.productSku || metaSku.textContent;
    }

    // Fallback: sku element
    if (!sku) {
      const skuEl = document.querySelector('.product__sku, [data-sku], .variant-sku');
      if (skuEl) sku = skuEl.textContent?.trim();
    }

    _cachedProductInfo = { sku: (sku || '').trim(), title };
    return _cachedProductInfo;
  }

  function getSelectedVariantId() {
    const input = document.querySelector('input[name="id"], [name="id"]');
    return input ? parseInt(input.value) : null;
  }

  /* ─────────────────────────────────────────────
     POPUP
  ───────────────────────────────────────────── */

  // Set to true while we're programmatically re-submitting after a popup action
  let _swSubmitting = false;

  // Window-level set so it persists even if the script somehow re-executes.
  // Keys are variantId or a fallback string. Prevents CandyRack and similar
  // upsell apps re-triggering the popup when they re-submit the form.
  window.__swValidated = window.__swValidated || new Set();

  let popupResolve = null;

  function buildPopupCSS() {
    if (document.getElementById('sw-popup-style')) return;
    const style = document.createElement('style');
    style.id = 'sw-popup-style';
    style.textContent = `
      #sw-overlay{position:fixed;inset:0;background:rgba(26,22,16,.55);z-index:99998;display:flex;align-items:center;justify-content:center;padding:20px;backdrop-filter:blur(2px);}
      #sw-popup{background:#fbfaf7;border:1px solid #ece6d9;border-radius:8px;max-width:420px;width:100%;padding:28px 28px 22px;box-shadow:0 20px 60px rgba(26,22,16,.18);font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif;position:relative;text-align:center;}
      #sw-popup .sw-icon{font-size:22px;margin-bottom:12px;}
      #sw-popup h3{margin:0 0 10px;font-size:17px;font-weight:600;color:#2a2722;line-height:1.3;}
      #sw-popup p{margin:0 0 18px;font-size:14px;color:#6b6b6b;line-height:1.55;}
      #sw-popup p strong{color:#2a2722;}
      #sw-popup .sw-actions{display:flex;flex-direction:column;gap:8px;}
      #sw-popup .sw-btn{border:none;border-radius:6px;padding:11px 16px;font-size:14px;font-weight:600;cursor:pointer;font-family:inherit;text-align:center;transition:background .18s ease;}
      #sw-popup .sw-btn-primary{background:#1a1a1a;color:#fff;}
      #sw-popup .sw-btn-primary:hover{background:#b7a17a;}
      #sw-popup .sw-btn-secondary{background:#f0ece3;color:#2a2722;}
      #sw-popup .sw-btn-secondary:hover{background:#e5e0d4;}
      #sw-popup .sw-btn-ghost{background:none;color:#a99f8b;font-size:13px;font-weight:400;padding:6px 0;text-align:center;}
      #sw-popup .sw-btn-ghost:hover{color:#2a2722;}
      #sw-popup .sw-close{position:absolute;top:14px;right:16px;background:none;border:none;font-size:20px;cursor:pointer;color:#a99f8b;line-height:1;}
      #sw-popup .sw-close:hover{color:#2a2722;}
    `;
    document.head.appendChild(style);
  }

  /**
   * Show popup. Returns a promise resolving to:
   *   'swap'   — change to correct size
   *   'keep'   — keep wrong size (add override property)
   *   'no-variant' case: 'keep'
   */
  function showMismatchPopup({ watchLabel, watchMm, selectedMm, noVariant }) {
    // If a popup is already visible (e.g. CandyRack re-submission fires while popup is open),
    // silently resolve as 'keep' so we never show two popups simultaneously.
    if (window.__swPopupActive) return Promise.resolve('keep');
    window.__swPopupActive = true;

    buildPopupCSS();
    return new Promise((resolve) => {
      popupResolve = resolve;
      const _done = (action) => {
        window.__swPopupActive = false;
        popupResolve = null;
        resolve(action);
      };

      const overlay = document.createElement('div');
      overlay.id = 'sw-overlay';

      const watchStr   = `<strong>${watchLabel}</strong>`;
      const correctStr = `<strong>${watchMm}mm</strong>`;
      const wrongStr   = `<strong>${selectedMm}mm</strong>`;

      let title, body, actions;

      if (noVariant) {
        title = `Size mismatch for your watch`;
        body  = `Based on your selected watch:<br><strong>${watchLabel}</strong><br><br>Your correct strap size is ${correctStr}. Unfortunately this product doesn't come in ${correctStr}.<br><br>You can keep ${wrongStr} but it won't fit your watch.`;
        actions = `
          <button class="sw-btn sw-btn-secondary" data-action="keep">Keep ${selectedMm}mm anyway</button>
          <button class="sw-btn sw-btn-ghost"     data-action="keep">Cancel</button>
        `;
      } else {
        title = `Size mismatch for your watch`;
        body  = `Based on your selected watch:<br><strong>${watchLabel}</strong><br><br>Your correct strap size is ${correctStr}. You've selected ${wrongStr}, which won't fit your watch.`;
        actions = `
          <button class="sw-btn sw-btn-primary"   data-action="swap">Change to ${watchMm}mm</button>
          <button class="sw-btn sw-btn-secondary" data-action="keep">Keep ${selectedMm}mm (won't fit)</button>
          <button class="sw-btn sw-btn-ghost"     data-action="keep">Cancel</button>
        `;
      }

      overlay.innerHTML = `
        <div id="sw-popup">
          <button class="sw-close" data-action="keep" aria-label="Close">&times;</button>
          <div class="sw-icon">⚠️</div>
          <h3>${title}</h3>
          <p>${body}</p>
          <div class="sw-actions">${actions}</div>
        </div>
      `;

      overlay.querySelectorAll('[data-action]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const action = e.currentTarget.getAttribute('data-action');
          overlay.remove();
          _done(action);
        });
      });

      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          overlay.remove();
          _done('keep');
        }
      });

      document.body.appendChild(overlay);
    });
  }

  /* ─────────────────────────────────────────────
     ADD TO CART INTERCEPT
  ───────────────────────────────────────────── */

  async function handleAddToCart(e) {
    const watch = getSelectedWatch();
    if (!watch) return; // Scenario B — no watch selected, silent pass-through

    const { sku, title: productTitle } = getProductInfo();

    // Skip entirely for excluded SKU prefixes (s-, zx-, za-)
    if (!shouldInjectWatchNote(sku)) return;

    // Determine if we should validate
    const doValidate = shouldValidate(sku, productTitle);

    // Always write watch properties regardless of validation
    // (we'll set them after any potential swap)
    let proceedWithAdd = true;
    let sizeOverride   = false;

    if (doValidate) {
      const selectedSizeStr = getSelectedVariantSize();
      const selectedMm      = extractMm(selectedSizeStr);
      const watchMm         = extractMm(watch.size);

      console.log('[SW] Size check:', {selectedSizeStr, selectedMm, watchMm});

      if (watchMm !== null && selectedMm !== null && watchMm !== selectedMm) {
        const variantId = getSelectedVariantId();
        // Null-safe key: fall back to watch+size string if variant ID not readable from DOM
        const validationKey = variantId != null
          ? variantId
          : `${watch.brand}__${watch.model}__${selectedMm}`;

        if (window.__swValidated.has(validationKey)) {
          // User already actioned the popup for this variant (e.g. CandyRack re-submission)
          sizeOverride = true;
        } else {
          e.preventDefault();
          e.stopImmediatePropagation();

          const correctVariantEl = findVariantForMm(watchMm);
          const noVariant        = !correctVariantEl;

          const action = await showMismatchPopup({
            watchLabel: `${watch.brand} ${watch.model}`,
            watchMm,
            selectedMm,
            noVariant,
          });

          if (action === 'swap' && !noVariant) {
            activateVariant(correctVariantEl);
            await new Promise(r => setTimeout(r, 80));
          }

          if (action === 'keep') {
            sizeOverride = true;
          }

          // Mark as validated — window-level so it survives any re-execution of the IIFE
          window.__swValidated.add(validationKey);

          injectPropertiesAndSubmit(e.currentTarget.closest('form') || e.currentTarget, watch, sizeOverride);
          return;
        }
      }
    }

    // No validation needed or sizes match — inject properties and let form proceed
    if (proceedWithAdd) {
      injectProperties(e.currentTarget.closest('form') || document.querySelector('form[action="/cart/add"]'), watch, sizeOverride);
    }
  }

  /** Inject hidden inputs into the form for line item properties */
  function injectProperties(form, watch, sizeOverride) {
    if (!form) return;
    removeInjectedInputs(form);

    const props = {
      [`properties[${PROP_WATCH}]`]: `${watch.brand} ${watch.model}`,
      [`properties[${PROP_SIZE}]`]:  watch.size || '',
    };
    if (sizeOverride) {
      props[`properties[${PROP_OVERRIDE}]`] = 'Customer confirmed incorrect size';
    }

    for (const [name, value] of Object.entries(props)) {
      const input  = document.createElement('input');
      input.type   = 'hidden';
      input.name   = name;
      input.value  = value;
      input.setAttribute('data-sw-injected', '1');
      form.appendChild(input);
    }
  }

  /** Inject then programmatically submit (used after variant swap) */
  function injectPropertiesAndSubmit(formOrBtn, watch, sizeOverride) {
    if (!formOrBtn) return;
    let form = formOrBtn.tagName === 'FORM'
      ? formOrBtn
      : (formOrBtn.closest('form') || document.querySelector('form[action="/cart/add"]'));
    if (!form) return;
    injectProperties(form, watch, sizeOverride);
    const submitBtn = form.querySelector(
      'button[type="submit"][name="add"], button[name="add"], button.button--xl, button[is="custom-button"]'
    );
    _swSubmitting = true;
    if (submitBtn) {
      submitBtn.click();
    } else {
      form.submit();
    }
    setTimeout(() => { _swSubmitting = false; }, 1000);
  }

  function removeInjectedInputs(form) {
    if (!form) return;
    form.querySelectorAll('[data-sw-injected]').forEach(el => el.remove());
  }

  /* ─────────────────────────────────────────────
     CART — "For Watch" BADGE + REMOVE
  ───────────────────────────────────────────── */

  function buildCartBadgeCSS() {
    if (document.getElementById('sw-cart-style')) return;
    const style = document.createElement('style');
    style.id = 'sw-cart-style';
    style.textContent = `
      .sw-watch-badge{display:inline-flex;align-items:center;gap:6px;margin-top:4px;font-size:12px;color:#8d7b52;background:#f6f1e8;border:1px solid #e0d8c8;border-radius:4px;padding:3px 8px;font-family:'Inter',-apple-system,sans-serif;}
      .sw-watch-badge .sw-watch-label{font-weight:500;}
      .sw-watch-badge .sw-remove{background:none;border:none;cursor:pointer;color:#a99f8b;font-size:14px;line-height:1;padding:0 0 0 2px;transition:color .15s ease;}
      .sw-watch-badge .sw-remove:hover{color:#2a2722;}
    `;
    document.head.appendChild(style);
  }

  /**
   * Render "For Watch" badges in the cart.
   * Looks for line items that have a "For Watch" property displayed,
   * and appends a removable badge if not already present.
   * Works by scanning cart item property nodes — theme-agnostic approach.
   */
  function renderCartBadges() {
    buildCartBadgeCSS();

    // Impulse theme: properties render as <li class="text-sm text-subdued"> inside <line-item>
    const lis = document.querySelectorAll('line-item li');
    for (const li of lis) {
      const text = li.textContent?.trim() || '';
      if (!/^For Watch\s*:/i.test(text)) continue;
      if (li.querySelector('.sw-watch-badge')) continue;

      const watchValue = text.replace(/^For Watch\s*:\s*/i, '').trim();

      // Get line key from the parent line-item's quantity input
      const lineItem = li.closest('line-item');
      const lineKey  = lineItem?.querySelector('[data-line-key]')?.getAttribute('data-line-key');
      if (!lineKey) continue;

      li.style.listStyle = 'none';
      li.textContent = '';

      const badge = document.createElement('span');
      badge.className = 'sw-watch-badge';
      badge.innerHTML = `<span class="sw-watch-label">⌚ ${watchValue}</span><button class="sw-remove" title="Remove watch" aria-label="Remove watch selection">×</button>`;
      badge.querySelector('.sw-remove').addEventListener('click', async (ev) => {
        ev.preventDefault();
        await removeWatchProperties(lineKey);
      });
      li.appendChild(badge);
    }
  }

  async function removeWatchProperties(lineKey) {
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      const item = cart.items.find(i => i.key === lineKey);
      if (!item) return;

      const newProps = {};
      for (const [k, v] of Object.entries(item.properties || {})) {
        if (k !== PROP_WATCH && k !== PROP_SIZE && k !== PROP_OVERRIDE) newProps[k] = v;
      }

      await fetch('/cart/change.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ id: lineKey, properties: newProps }),
      });

      // Impulse cart refresh
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:updated'));
      // Fallback: reload if cart drawer doesn't re-render on its own
      setTimeout(() => {
        if (document.querySelector(`[data-line-key="${lineKey}"]`)) window.location.reload();
      }, 600);
    } catch (err) {
      console.error('[SW] Failed to remove watch properties:', err);
    }
  }

  /* ─────────────────────────────────────────────
     INIT
  ───────────────────────────────────────────── */

  /**
   * Attach the Add to Cart interceptor.
   * Uses event delegation on document so it works with dynamically
   * loaded quick-add forms and cart drawers.
   */
  function initAddToCartListener() {
    document.addEventListener('click', async (e) => {
      if (_swSubmitting) return; // our own programmatic re-submit — skip

      const btn = e.target.closest(
        'button[type="submit"][name="add"], button[name="add"], [data-add-to-cart], .product-form__submit, button.button--xl, button[is="custom-button"]'
      );
      if (!btn) return;

      const form = btn.closest('form[action*="/cart/add"], form[action="/cart/add"]');
      if (!form) return;

      await handleAddToCart({ preventDefault: () => e.preventDefault(), stopImmediatePropagation: () => e.stopImmediatePropagation(), currentTarget: btn });
    }, true);
  }

  /**
   * Watch for cart DOM changes and render badges.
   * Covers cart page loads, drawer opens, and AJAX cart updates.
   */
  function initCartObserver() {
    // Run on load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', renderCartBadges);
    } else {
      renderCartBadges();
    }

    // Debounced observer — prevents firing hundreds of times per DOM update burst
    let _badgeTimer = null;
    const observer = new MutationObserver(() => {
      if (_badgeTimer) return;
      _badgeTimer = setTimeout(() => { _badgeTimer = null; renderCartBadges(); }, 200);
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Common theme cart events
    ['cart:refresh', 'cart:updated', 'theme:cart:open'].forEach(evt => {
      document.addEventListener(evt, () => setTimeout(renderCartBadges, 200));
    });
  }

  /**
   * Load product JSON into a global so getProductInfo() can read SKUs.
   * Most Shopify themes expose product JSON in a <script> tag.
   */
  function loadProductJSON() {
    // Dawn and most modern themes
    const jsonEl = document.querySelector(
      'script[type="application/json"][data-product-json], #ProductJson-product-template, script#product-json'
    );
    if (jsonEl) {
      try { window.__strapify_productJSON = JSON.parse(jsonEl.textContent); } catch (e) {}
    }
    // Fallback: window.ShopifyAnalytics
    if (!window.__strapify_productJSON && window.ShopifyAnalytics?.meta?.product) {
      window.__strapify_productJSON = window.ShopifyAnalytics.meta.product;
    }
    // Fallback: window.theme.product (some themes)
    if (!window.__strapify_productJSON && window.theme?.product) {
      window.__strapify_productJSON = window.theme.product;
    }
  }

  function init() {
    loadProductJSON();
    initAddToCartListener();
    initCartObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
